import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import moment from 'moment';

import ScorecardForm from './ScorecardForm';
import { useCollaboratorNames } from '../../../components/Goals/GeneralGoalForm';
import useStartDateOptions from './useStartDateOptions';
import updateScorecardDoc from '../updateScorecardDoc';
import CurrentUserContext from '../../../contexts/CurrentUserContext';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import LocalTimelineProvider from '../../../contextProviders/TimelineProvider/LocalTimelineProvider';
import LocalTimelineContext from '../../../contexts/Timeline/LocalTimelineContext';
import AnalyticsContext from '../../../contexts/AnalyticsContext';
import usePopup from '../../../hooks/usePopup';
import ConfirmationModal from '../../../components/ConfirmationModal';
import Typography from 'kingpin/atoms/Typography';
import { Link } from 'react-router-dom';
import { buildDashboardShow } from '../../../navigation/appRoutes';
import useScorecardUsageReport from '../../../components/Scorecard/useScorecardUsageReport';
import targetDateToString from '../../../types/scorecardDates/targetDateToString';
import getIdentifier from '../../../getIdentifier';
import getTimeStamp from '../../../getTimeStamp';
import {
  CURRENT_SCORECARD_VERSION,
  FISCAL_PERIODS_DISABLED_TOOLTIP,
  SCORECARD_RESOURCE,
} from '../../../constants';
import useCopyVisHelpers from '../../../hooks/useCopyVisHelpers';
import { isKpiRow, isManualKpiRow } from 'hooks/kpiTypeCheckers';
import useContentSharedEmails from '../../../hooks/useContentSharedEmails';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { UIVersionContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import {
  migrateScorecardIsV3Published,
  migrateScorecardV3Access,
} from '../../Settings/Advanced/V3Migrators/ContentV3AccessMigrator/utils';
import { FiscalPeriodsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';
import FeatureGatesContext from '../../../contexts/FeatureGatesContext';
import { AccountTodayOverrideContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import { buildFiscalPeriods } from '../../../contextProviders/ScorecardProvider/buildScorecardPeriods';

const ConfirmationBody = ({ usage }: { usage: Scorecards.UsageReport }) => {
  const { selectedPortal } = useContext(PortalsContext);

  return (
    <div style={{ marginBottom: 32 }}>
      {usage.dashboards.length === 0 && (
        <Typography.Body type="Body 12">
          This KPI List does not appear in any dashboards.
        </Typography.Body>
      )}
      {usage.dashboards.length >= 1 && (
        <>
          <Typography.Body type="Body 12">
            This KPI List appears in the following dashboards:
          </Typography.Body>
          {usage.dashboards.map((d) => (
            <div key={d.id}>
              <Link
                to={buildDashboardShow({
                  id: d.id,
                  portal: selectedPortal,
                })}
                target="_blank"
              >
                <Typography.Body type="Link">{`- ${d.name}`}</Typography.Body>
              </Link>
            </div>
          ))}
        </>
      )}

      <Typography.Body type="Body 12">
        By making this change, the targets for this KPI List will be lost and
        need to be reset
      </Typography.Body>
    </div>
  );
};

const ScorecardFormContainer = ({
  id,
  close,
  scorecard,
  isCopyingScorecard,
  onScorecardCreated,
}: {
  id: string;
  close: () => void;
  scorecard?: Scorecards.Scorecard;
  isCopyingScorecard?: boolean;
  onScorecardCreated?: (newScorecardId: string) => void;
}) => {
  const {
    isOpen: isSaveConfirmationOpen,
    open: openSaveConfirmation,
    close: closeSaveConfirmation,
  } = usePopup();
  const { isFiscalPeriodsEnabled } = useContext(FeatureGatesContext);
  const { isV3 } = useContext(UIVersionContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { addEvent } = useContext(LocalTimelineContext);
  const currentUser = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { fiscalPeriods, currentFiscalPeriod } =
    useContext(FiscalPeriodsContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const [isEditing] = useState(!!scorecard);
  const { usage } = useScorecardUsageReport(scorecard);
  const { sendContentSharedEmails } = useContentSharedEmails();
  const { getMetricIdForNewVis } = useCopyVisHelpers();
  const [areKpisSet] = useState<boolean>(
    scorecard ? scorecard.kpis.length > 0 : false,
  );
  const [mustResetTargets, setMustResetTargets] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(
    scorecard
      ? isCopyingScorecard
        ? `${scorecard.title} (Copy)`
        : scorecard.title
      : '',
  );
  const [labelIds, setLabelIds] = useState<string[]>(
    scorecard ? scorecard.labelIds : [],
  );
  const [assigneeId, setAssigneeId] = useState<string[]>(
    scorecard && !isCopyingScorecard ? scorecard.assigneeId : [],
  );

  const [weekStartsOnOverride, setWeekStartsOnOverride] = useState<
    WeekStartsOn | undefined
  >(scorecard ? scorecard.weekStartsOnOverride : undefined);
  const [startDate, setStartDate] = useState<
    Scorecards.IsoDate | Scorecards.WeekDate | undefined
  >(scorecard ? scorecard.startDate : undefined);
  const [defaultSelectedNumberOfPeriods, setDefaultSelectedNumberOfPeriods] =
    useState<number | undefined>(
      scorecard ? scorecard.defaultSelectedNumberOfPeriods || 12 : 12,
    );
  const [cadence, setCadence] = useState<'week' | 'month' | 'fiscalPeriod'>(
    scorecard ? scorecard.cadence : 'week',
  );
  const [currentPeriodEndDate, setCurrentPeriodEndDate] = useState<
    'yesterday' | 'today' | 'period'
  >(scorecard?.currentPeriodEndDate ?? 'yesterday');
  const [numberOfFuturePeriods, setNumberOfFuturePeriods] = useState<
    number | undefined
  >(() => {
    if (!scorecard) {
      return 0;
    }
    if (scorecard.numberOfFuturePeriods !== undefined) {
      return scorecard.numberOfFuturePeriods;
    }
    // Legacy fiscal scorecards with a start date may already show future
    // periods. Carry that count over so saving doesn't change the columns.
    if (scorecard.cadence === 'fiscalPeriod' && scorecard.startDate) {
      const now = moment.utc(todayOverride).startOf('day').format('YYYY-MM-DD');
      return buildFiscalPeriods({
        scorecard,
        now,
        fiscalPeriods,
        currentFiscalPeriod,
      }).filter((p) => p.startDate > now).length;
    }
    return 0;
  });
  const [cadenceOptions, setCadenceOptions] = useState<RadioOption[]>([]);
  useEffect(() => {
    const cadenceOptions = [
      {
        key: 'week',
        label: 'Week',
        onSelected: () => {
          setCadence('week');
          setStartDate(undefined);
        },
        isSelected: cadence === 'week',
      },
      {
        key: 'month',
        label: 'Month',
        onSelected: () => {
          setCadence('month');
          setStartDate(undefined);
        },
        isSelected: cadence === 'month',
      },
      {
        key: 'fiscalPeriod',
        label: 'Fiscal Period',
        isDisabled: !fiscalPeriods?.length || !isFiscalPeriodsEnabled,
        tooltip: !isFiscalPeriodsEnabled
          ? FISCAL_PERIODS_DISABLED_TOOLTIP
          : undefined,
        onSelected: () => {
          setCadence('fiscalPeriod');
          setStartDate(undefined);
        },
        isSelected: cadence === 'fiscalPeriod',
      },
    ];

    setCadenceOptions(cadenceOptions);
  }, [cadence, fiscalPeriods, isFiscalPeriodsEnabled]);

  const [currentPeriodEndDateOptions, setCurrentPeriodEndDateOptions] =
    useState<RadioOption[]>([]);
  useEffect(() => {
    setCurrentPeriodEndDateOptions([
      {
        key: 'yesterday',
        label: 'Through yesterday',
        tooltip:
          "The current period shows data up to and including yesterday. Use this when your data pipeline updates overnight and today's figures are incomplete.",
        onSelected: () => setCurrentPeriodEndDate('yesterday'),
        isSelected: currentPeriodEndDate === 'yesterday',
      },
      {
        key: 'today',
        label: 'Including today',
        tooltip:
          "The current period includes today's data, matching how dashboards display the current period. Use this when your data updates throughout the day.",
        onSelected: () => setCurrentPeriodEndDate('today'),
        isSelected: currentPeriodEndDate === 'today',
      },
      {
        key: 'period',
        label: 'Entire period',
        tooltip:
          'The current period shows data for the whole period, including future-dated records (e.g. planned loads). Use this for KPIs on scheduled or forecast data.',
        onSelected: () => setCurrentPeriodEndDate('period'),
        isSelected: currentPeriodEndDate === 'period',
      },
    ]);
  }, [currentPeriodEndDate]);

  const [scoringBandId, setScoringBandId] = useState<Scorecards.ScoringBandId>(
    scorecard ? scorecard.scoringBandId : 1,
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    setIsValid(title !== '' && !!defaultSelectedNumberOfPeriods);
  }, [defaultSelectedNumberOfPeriods, title]);

  useEffect(() => {
    setMustResetTargets(
      !!areKpisSet &&
        !!scorecard &&
        (scorecard.scoringBandId !== scoringBandId ||
          scorecard.cadence !== cadence),
    );
  }, [areKpisSet, cadence, scorecard, scoringBandId]);

  const assigneeName = useCollaboratorNames(assigneeId);
  const { startDateOptions, startDateLabel, todayOption } = useStartDateOptions(
    {
      cadence,
      startDate,
      setStartDate,
      weekStartsOnOverride,
      isClearable: true,
    },
  );

  const onTitleChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  }, []);

  const trackSaved = useCallback(
    ({
      event,
      newScorecard,
    }: {
      event: Analytics.EventType;
      newScorecard: Scorecards.Scorecard;
    }) => {
      const numAssigned = newScorecard.assigneeId.length.toString();
      const interval = newScorecard.cadence;
      const startFrom = newScorecard.startDate;
      const scoringBandId = newScorecard.scoringBandId.toString();
      trackEvent(event, {
        numAssigned,
        interval,
        startFrom: startFrom ? targetDateToString(startFrom) : undefined,
        scoringBandId,
      });
    },
    [trackEvent],
  );

  const onSaveConfirmed = useCallback(async () => {
    if (!isValid) {
      return;
    }

    setIsSaving(true);
    const numberOfFuturePeriodsToSave = Math.max(
      0,
      Math.floor(numberOfFuturePeriods || 0),
    );
    if (isEditing && !!scorecard && !isCopyingScorecard) {
      let updatedScorecard: Scorecards.Scorecard = {
        ...scorecard,
        title,
        assigneeId,
        cadence,
        weekStartsOnOverride,
        startDate,
        defaultSelectedNumberOfPeriods,
        scoringBandId,
        currentPeriodEndDate,
        numberOfFuturePeriods: numberOfFuturePeriodsToSave,
        // Superseded by numberOfFuturePeriods
        ignoreFuturePeriods: undefined,
        updatedBy: currentUser.id,
        updatedOn: getTimeStamp(),
        access: {
          version: '2',
          type: 'Users',
          userIds: assigneeId,
        },
        isV3Published: true,
        v3Access: {
          userIds: assigneeId,
          isPublic: false,
          groupIds: [],
        },
        kpis: !mustResetTargets
          ? scorecard.kpis
          : scorecard.kpis.map((kpi) => {
              if (isKpiRow(kpi) || isManualKpiRow(kpi)) {
                return {
                  ...kpi,
                  targets:
                    cadence === 'week'
                      ? ({
                          mode: 'Week',
                          targets: {},
                        } as Scorecards.WeekTargets)
                      : ({
                          mode: 'ISO',
                          targets: {},
                        } as Scorecards.IsoTargets),
                  isTargetsDisabled: true,
                };
              }
              return kpi;
            }),
        labelIds,
      };

      if (!isV3) {
        updatedScorecard.v3Access = migrateScorecardV3Access(updatedScorecard);
        updatedScorecard.isV3Published =
          migrateScorecardIsV3Published(updatedScorecard);
      }
      await updateScorecardDoc(updatedScorecard, selectedAccountId);
      await sendContentSharedEmails({
        type: 'Assigned List',
        initialAssigned: scorecard.assigneeId,
        newAssigned: assigneeId,
      });
      trackSaved({
        event: 'KPI List - Edit Saved',
        newScorecard: updatedScorecard,
      });
      setIsSaving(false);
      close();
    } else {
      const newKpis = await (async () => {
        if (scorecard && isCopyingScorecard) {
          const kpis = await Promise.all(
            scorecard.kpis.map(async (kpi) => {
              if (isKpiRow(kpi)) {
                return {
                  ...kpi,
                  id: getIdentifier(),
                  metricId: await getMetricIdForNewVis(kpi.metricId),
                };
              }
              return kpi;
            }),
          );

          if (!mustResetTargets) {
            return kpis;
          }

          return kpis.map((kpi) => ({
            ...kpi,
            targets:
              cadence === 'week'
                ? ({ mode: 'Week', targets: {} } as Scorecards.WeekTargets)
                : ({ mode: 'ISO', targets: {} } as Scorecards.IsoTargets),
            weeklyTargets: undefined,
            isTargetsDisabled: true,
          }));
        }

        return [];
      })();

      let newScorecard: Scorecards.Scorecard = {
        type: 'Scorecard' as 'Scorecard',
        version: CURRENT_SCORECARD_VERSION,
        id,
        title,
        assigneeId,
        access: {
          version: '2',
          type: 'Users',
          userIds: assigneeId,
        },
        isV3Published: true,
        v3Access: {
          userIds: assigneeId,
          isPublic: false,
          groupIds: [],
        },
        cadence,
        weekStartsOnOverride,
        startDate,
        defaultSelectedNumberOfPeriods,
        scoringBandId,
        currentPeriodEndDate,
        numberOfFuturePeriods: numberOfFuturePeriodsToSave,
        labelIds,
        kpis: newKpis,
        createdBy: currentUser.id,
        updatedBy: currentUser.id,
        createdOn: getTimeStamp(),
        updatedOn: getTimeStamp(),
      };
      if (!isV3) {
        newScorecard.v3Access = migrateScorecardV3Access(newScorecard);
        newScorecard.isV3Published =
          migrateScorecardIsV3Published(newScorecard);
      }

      await updateScorecardDoc(newScorecard, selectedAccountId);
      await sendContentSharedEmails({
        type: 'Assigned List',
        initialAssigned: undefined,
        newAssigned: assigneeId,
      });
      if (addEvent) {
        await addEvent({
          actionText: 'created the KPI List',
          contextText: title,
        });
      }

      trackSaved({
        event: isCopyingScorecard
          ? 'KPI List - Copy Saved'
          : 'KPI List - Created',
        newScorecard,
      });
      setIsSaving(false);
      if (onScorecardCreated) {
        onScorecardCreated(newScorecard.id);
      }
      close();
    }
  }, [
    addEvent,
    assigneeId,
    cadence,
    close,
    currentPeriodEndDate,
    currentUser.id,
    defaultSelectedNumberOfPeriods,
    getMetricIdForNewVis,
    id,
    isCopyingScorecard,
    isEditing,
    isV3,
    isValid,
    labelIds,
    mustResetTargets,
    numberOfFuturePeriods,
    onScorecardCreated,
    scorecard,
    scoringBandId,
    selectedAccountId,
    sendContentSharedEmails,
    startDate,
    title,
    trackSaved,
    weekStartsOnOverride,
  ]);

  const onSaveClicked = useCallback(() => {
    if (mustResetTargets) {
      openSaveConfirmation();
    } else {
      onSaveConfirmed();
    }
  }, [mustResetTargets, onSaveConfirmed, openSaveConfirmation]);

  return (
    <>
      <ScorecardForm
        isEditing={isEditing}
        close={close}
        title={title}
        onTitleChanged={onTitleChanged}
        assigneeId={assigneeId}
        setAssigneeId={setAssigneeId}
        assigneeName={assigneeName}
        cadence={cadence}
        cadenceOptions={cadenceOptions}
        startDateLabel={startDateLabel}
        startDateOptions={startDateOptions}
        scoringBandId={scoringBandId}
        setScoringBandId={setScoringBandId}
        isValid={isValid}
        onSaveClicked={onSaveClicked}
        isSaving={isSaving}
        isCopyingScorecard={isCopyingScorecard}
        scorecard={scorecard}
        weekStartsOnOverride={weekStartsOnOverride}
        setWeekStartsOnOverride={setWeekStartsOnOverride}
        todayOption={todayOption}
        defaultSelectedNumberOfPeriods={defaultSelectedNumberOfPeriods}
        setDefaultSelectedNumberOfPeriods={setDefaultSelectedNumberOfPeriods}
        labelIds={labelIds}
        setLabelIds={setLabelIds}
        currentPeriodEndDateOptions={currentPeriodEndDateOptions}
        numberOfFuturePeriods={numberOfFuturePeriods}
        setNumberOfFuturePeriods={setNumberOfFuturePeriods}
      />
      <ConfirmationModal
        isOpen={isSaveConfirmationOpen}
        close={closeSaveConfirmation}
        title={'Remove targets?'}
        confirmText={'Update and remove targets'}
        bodyComponent={<ConfirmationBody usage={usage} />}
        onConfirmed={onSaveConfirmed}
      />
    </>
  );
};

const Gate = ({
  close,
  scorecard,
  isCopyingScorecard,
  onScorecardCreated,
}: {
  close: () => void;
  scorecard?: Scorecards.Scorecard;
  isCopyingScorecard?: boolean;
  onScorecardCreated?: (newScorecardId: string) => void;
}) => {
  const [id] = useState<string>(
    scorecard && !isCopyingScorecard ? scorecard.id : getIdentifier(),
  );

  const [sources, setSources] = useState<Timeline.EventSource[]>([]);
  const [destination, setDestination] = useState<Timeline.EventSource>();

  useEffect(() => {
    setSources([{ type: SCORECARD_RESOURCE, id }]);
    setDestination({ type: SCORECARD_RESOURCE, id });
  }, [id]);

  return (
    <LocalTimelineProvider sources={sources} destination={destination}>
      <ScorecardFormContainer
        id={id}
        close={close}
        scorecard={scorecard}
        isCopyingScorecard={isCopyingScorecard}
        onScorecardCreated={onScorecardCreated}
      />
    </LocalTimelineProvider>
  );
};

export default Gate;
